import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import { IBudgetUnitAddEditRequest } from '../../../../model/sge/budget-unit/request/budget-unit-add-edit-request';
import { IBudgetUnitResponse } from '../../../../model/sge/budget-unit/response/budget-unit-response';
import { hasErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { toBudgetUnitForm, validateBudgetUnit } from './budget-unit-form-utils';
import { formatCodeName } from '../../components/code-name-label';

interface BudgetUnitDrawerProps {
    open: boolean;
    initial?: IBudgetUnitResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IBudgetUnitAddEditRequest) => void;
}

export function BudgetUnitDrawer({ open, initial, isSaving, onClose, onSave }: BudgetUnitDrawerProps) {
    const [form, setForm] = useState<IBudgetUnitAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateBudgetUnit(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toBudgetUnitForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IBudgetUnitAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, code: form.code!.trim(), name: form.name!.trim() });
    }

    return (
        <FormDrawer
            open={open}
            icon={<AccountTreeIcon />}
            title={isEdit ? 'Editar unidade orçamentária' : 'Nova unidade orçamentária'}
            subtitle={initial ? `${formatCodeName(initial)} · Exercício ${initial.year}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar unidade'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Exercício</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="number"
                            autoComplete="off"
                            disabled={isEdit}
                            value={form.year ?? ''}
                            onChange={(e) => update({ year: parseNumberInput(e.target.value) })}
                            error={!!errors.year}
                        />
                        <FieldError message={errors.year} />
                        {isEdit && <FormHelperText>O exercício não pode ser alterado</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Código</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.code || ''} onChange={(e) => update({ code: e.target.value })} error={!!errors.code} />
                        <FieldError message={errors.code} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Nome</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.name || ''} onChange={(e) => update({ name: e.target.value })} error={!!errors.name} />
                        <FieldError message={errors.name} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
